import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { type ContractSuggestion } from './types';
import { zodContractSuggestionsResponseSchema } from './validations';

/**
 * Fetches matching contract suggestions for a given delivery note.
 *
 * Results are ranked by match strength (lower is stronger). Rank 0 is a synthetic
 * rank for the currently linked contract when it no longer matches; ranks 1–3 are
 * order-reference and accounting-reference matches. Additional ranks may appear.
 * When `isCurrent` is true, prefer that flag over `matchRank`.
 *
 * @param assetId - The delivery note asset ID.
 * @param options - Optional ky request options (e.g. abort signal).
 * @returns Array of contract suggestions sorted by match rank.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_contract_suggestions_delivery_note__asset_id__contract_suggestions_get
 */
export const fetchContractSuggestions = async (
  assetId: UUID,
  options?: KyRequestOptions,
) =>
  vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_CONTRACT_SUGGESTIONS(assetId), {
      ...options,
      validate: {
        ...options?.validate,
        response: zodContractSuggestionsResponseSchema,
      },
    })
    .json<ContractSuggestion[]>();
